import { useEffect, useRef, useState } from 'react'
import { supabase } from './supabaseClient'
import './Onboarding.css'

const TOTAL_STEPS = 4
const GRAD_YEARS  = [2025, 2026, 2027, 2028, 2029, 2030]
const POSITIONS   = ['P', 'C', '1B', '2B', '3B', 'SS', 'OF', 'UTIL', 'DH']

const DIV_LABEL = { D1: 'D1', D2: 'D2', D3: 'D3', JUCO: 'JUCO' }
const DIV_CLASS = { D1: 'ob-badge-d1', D2: 'ob-badge-d2', D3: 'ob-badge-d3', JUCO: 'ob-badge-juco' }

export default function Onboarding({ user, onComplete }) {
  const [step, setStep] = useState(1)

  // Step 1
  const [fullName,  setFullName]  = useState('')
  const [nameError, setNameError] = useState(null)

  // Step 2
  const [gradYear,   setGradYear]   = useState('')
  const [position,   setPosition]   = useState('')
  const [highSchool, setHighSchool] = useState('')
  const [hometown,   setHometown]   = useState('')
  const [zipCode,    setZipCode]    = useState('')

  // Step 3 — school search
  const [schoolQuery,    setSchoolQuery]    = useState('')
  const [searchResults,  setSearchResults]  = useState([])
  const [searching,      setSearching]      = useState(false)
  const [selectedSchool, setSelectedSchool] = useState(null)
  const searchRef = useRef(null)

  // Shared
  const [saving, setSaving] = useState(false)

  // ── School search debounce ─────────────────────────────────────────

  useEffect(() => {
    if (!schoolQuery.trim() || selectedSchool) { setSearchResults([]); return }
    const timer = setTimeout(async () => {
      setSearching(true)
      const { data } = await supabase
        .from('schools_db')
        .select('id, school_name, division')
        .ilike('school_name', `%${schoolQuery.trim()}%`)
        .limit(8)
      setSearchResults(data || [])
      setSearching(false)
    }, 250)
    return () => clearTimeout(timer)
  }, [schoolQuery, selectedSchool])

  // ── Handlers ──────────────────────────────────────────────────────

  function selectSchool(s) {
    setSelectedSchool(s)
    setSchoolQuery(s.school_name)
    setSearchResults([])
  }

  function clearSchool() {
    setSelectedSchool(null)
    setSchoolQuery('')
    setTimeout(() => searchRef.current?.focus(), 0)
  }

  function handleSearchBlur() { setTimeout(() => setSearchResults([]), 150) }

  async function handleNext() {
    if (step === 1) {
      if (!fullName.trim()) { setNameError('Please enter your full name.'); return }
      setSaving(true)
      await supabase.from('profiles').upsert(
        { id: user.id, full_name: fullName.trim(), updated_at: new Date().toISOString() },
        { onConflict: 'id' }
      )
      setSaving(false)
    }

    if (step === 2) {
      setSaving(true)
      await supabase.from('profiles').upsert(
        {
          id:          user.id,
          grad_year:   gradYear          || null,
          position:    position          || null,
          high_school: highSchool.trim() || null,
          hometown:    hometown.trim()   || null,
          zip_code:    zipCode.trim()    || null,
          updated_at:  new Date().toISOString(),
        },
        { onConflict: 'id' }
      )

      // Geocode zip code and save coords immediately
      if (zipCode.trim().length === 5) {
        try {
          const res  = await fetch(
            `https://nominatim.openstreetmap.org/search?q=${zipCode.trim()}&countrycodes=us&format=json&limit=1`
          )
          const json = await res.json()
          if (json?.[0]) {
            const lat = parseFloat(json[0].lat)
            const lng = parseFloat(json[0].lon)
            await supabase
              .from('profiles')
              .update({ home_lat: lat, home_lng: lng })
              .eq('id', user.id)
          }
        } catch (_) {}
      }

      setSaving(false)
    }

    if (step === 3) {
      if (selectedSchool) {
        setSaving(true)
        await supabase.from('pipeline_schools').insert({
          user_id:     user.id,
          school_name: selectedSchool.school_name,
          division:    selectedSchool.division,
          sport:       'Baseball',
          status:      'Researching',
        })
        setSaving(false)
      }
      setStep(4)
      return
    }

    if (step === 4) {
      setSaving(true)
      await supabase.from('profiles').upsert(
        { id: user.id, onboarding_complete: true, updated_at: new Date().toISOString() },
        { onConflict: 'id' }
      )
      setSaving(false)
      onComplete()
      return
    }

    setStep(s => s + 1)
  }

  function handleBack() {
    setNameError(null)
    setStep(s => s - 1)
  }

  // ── Render ─────────────────────────────────────────────────────────

  return (
    <div className="ob-overlay">
      <div className="ob-card">

        {/* Progress bar */}
        <div className="ob-progress-bar">
          <div
            className="ob-progress-fill"
            style={{ width: `${(step / TOTAL_STEPS) * 100}%` }}
          />
        </div>
        <p className="ob-step-label">Step {step} of {TOTAL_STEPS}</p>

        {/* ── Step 1: Welcome ── */}
        {step === 1 && (
          <div className="ob-step">
            <div className="ob-logo">
              <svg width="48" height="54" viewBox="0 0 64 72" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="64" height="72" rx="10" fill="#0D2340"/>
                <rect width="64" height="10" fill="#1D9E75"/>
                <rect width="64" height="5" y="5" fill="#1D9E75"/>
                <text x="32" y="50" textAnchor="middle" fontFamily="Georgia, serif" fontSize="34" fontWeight="bold" fill="white">C</text>
                <text x="32" y="64" textAnchor="middle" fontFamily="Georgia, serif" fontSize="11" fontWeight="bold" fill="#1D9E75">HQ</text>
              </svg>
            </div>
            <h1 className="ob-headline">Welcome to CommitHQ</h1>
            <p className="ob-subtext">
              The smarter way to manage your baseball recruiting. Let's get your profile set up in 4 quick steps.
            </p>
            <div className="ob-field">
              <label className="ob-label">Full Name <span className="ob-required">*</span></label>
              <input
                className={`ob-input${nameError ? ' ob-input-error' : ''}`}
                type="text"
                placeholder="e.g. Jake Reynolds"
                value={fullName}
                onChange={e => { setFullName(e.target.value); setNameError(null) }}
                onKeyDown={e => { if (e.key === 'Enter') handleNext() }}
                autoFocus
              />
              {nameError && <p className="ob-error">{nameError}</p>}
            </div>
          </div>
        )}

        {/* ── Step 2: Athlete profile ── */}
        {step === 2 && (
          <div className="ob-step">
            <h1 className="ob-headline">Tell us about your athlete</h1>
            <p className="ob-subtext">All fields are optional — you can fill these in later from My Page.</p>
            <div className="ob-fields-grid">
              <div className="ob-field">
                <label className="ob-label">Graduation Year</label>
                <select
                  className="ob-select"
                  value={gradYear}
                  onChange={e => setGradYear(e.target.value)}
                >
                  <option value="">Select year</option>
                  {GRAD_YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
              <div className="ob-field">
                <label className="ob-label">Position</label>
                <select
                  className="ob-select"
                  value={position}
                  onChange={e => setPosition(e.target.value)}
                >
                  <option value="">Select position</option>
                  {POSITIONS.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div className="ob-field ob-field-full">
                <label className="ob-label">High School</label>
                <input
                  className="ob-input"
                  type="text"
                  placeholder="e.g. Lincoln High School"
                  value={highSchool}
                  onChange={e => setHighSchool(e.target.value)}
                />
              </div>
              <div className="ob-field ob-field-full">
                <label className="ob-label">Hometown</label>
                <input
                  className="ob-input"
                  type="text"
                  placeholder="e.g. Austin, TX"
                  value={hometown}
                  onChange={e => setHometown(e.target.value)}
                />
              </div>
              <div className="ob-field">
                <label className="ob-label">Home Zip Code</label>
                <input
                  className="ob-input"
                  type="text"
                  placeholder="Home Zip Code"
                  value={zipCode}
                  maxLength={5}
                  onChange={e => setZipCode(e.target.value.replace(/\D/g, ''))}
                />
              </div>
            </div>
          </div>
        )}

        {/* ── Step 3: First school ── */}
        {step === 3 && (
          <div className="ob-step">
            <h1 className="ob-headline">Add your first target school</h1>
            <p className="ob-subtext">
              Search our database of 806 baseball programs to start your pipeline.
            </p>
            <div className="ob-field">
              <label className="ob-label">Search Schools</label>
              {selectedSchool ? (
                <div className="ob-school-chip">
                  <div className="ob-school-chip-left">
                    <span className={`ob-div-badge ${DIV_CLASS[selectedSchool.division] ?? 'ob-badge-d3'}`}>
                      {DIV_LABEL[selectedSchool.division] ?? selectedSchool.division ?? '—'}
                    </span>
                    <span className="ob-school-chip-name">{selectedSchool.school_name}</span>
                  </div>
                  <button className="ob-school-chip-clear" onClick={clearSchool} aria-label="Remove">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                      <line x1="18" y1="6" x2="6" y2="18"/>
                      <line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                  </button>
                </div>
              ) : (
                <div className="ob-school-search-wrap">
                  <svg className="ob-search-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <circle cx="11" cy="11" r="8"/>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                  <input
                    ref={searchRef}
                    className="ob-input ob-input-search"
                    type="text"
                    placeholder="Type a school name…"
                    value={schoolQuery}
                    onChange={e => setSchoolQuery(e.target.value)}
                    onBlur={handleSearchBlur}
                    autoFocus
                  />
                  {searching && <span className="ob-search-spinner" />}
                  {searchResults.length > 0 && (
                    <ul className="ob-search-results">
                      {searchResults.map(s => (
                        <li key={s.id} className="ob-search-result" onMouseDown={() => selectSchool(s)}>
                          <span className={`ob-div-badge ${DIV_CLASS[s.division] ?? 'ob-badge-d3'}`}>
                            {DIV_LABEL[s.division] ?? s.division ?? '—'}
                          </span>
                          <span className="ob-result-name">{s.school_name}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {schoolQuery.trim().length > 1 && !searching && searchResults.length === 0 && (
                    <div className="ob-search-empty">No schools found for "{schoolQuery}"</div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Step 4: All set ── */}
        {step === 4 && (
          <div className="ob-step ob-step-final">
            <div className="ob-checkmark">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                <polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
            </div>
            <h1 className="ob-headline">You're ready to go!</h1>
            <p className="ob-subtext">Here's what you can do in CommitHQ:</p>
            <ul className="ob-feature-list">
              <li className="ob-feature-item">
                <span className="ob-feature-icon">🎯</span>
                <span>Track schools in your recruiting pipeline</span>
              </li>
              <li className="ob-feature-item">
                <span className="ob-feature-icon">👤</span>
                <span>Build your public athlete profile</span>
              </li>
              <li className="ob-feature-item">
                <span className="ob-feature-icon">📋</span>
                <span>Browse 806 college baseball programs</span>
              </li>
            </ul>
          </div>
        )}

        {/* ── Footer ── */}
        <div className="ob-footer">
          <div className="ob-footer-left">
            {step > 1 && (
              <button className="ob-btn-back" onClick={handleBack} disabled={saving}>
                ← Back
              </button>
            )}
          </div>
          <div className="ob-footer-right">
            {step === 3 && (
              <button className="ob-btn-skip" onClick={() => setStep(4)} disabled={saving}>
                Skip for now
              </button>
            )}
            <button
              className="ob-btn-next"
              onClick={handleNext}
              disabled={saving}
            >
              {saving
                ? 'Saving…'
                : step === 4
                  ? "Let's Go!"
                  : 'Next →'}
            </button>
          </div>
        </div>

      </div>
    </div>
  )
}
